import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/pro-solid-svg-icons";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "hooks/redux";
import { LocaleCurrency } from "helpers/Locale";

import Sheet from "components/sheet/Sheet";

import "elements/register/register.scss";
import classNames from "classnames";
import { IOrder } from "redux/reducers/models/order";
import { valueToString } from "helpers/Helpers";
import "./serviceFee.scss";
import { DeleteServiceFeeLineItem, PostServiceFeeLineItem } from "api/rpc/2024-04/facilityAdmin/cart/cart";

interface IServiceFeeState {
  serviceFeeActive: boolean;
}

interface IProps {
  totalServiceFeeAmount: number;
  handleAddServiceFee?: (serviceFeeId: number) => void;
  totalCartPrice?: number;
  darkStyle?: boolean;
  order?: IOrder;
  darkerButton?: boolean;
  smallerText?: boolean;
  currency?: string;
}

// Using new component/folder that I added to the components folder
export default function ServiceFee(props: IProps) {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();
  const { cartStore, facilityStore } = useAppSelector(store => store);

  const [serviceFeeState, setServiceFeeState] = useState<IServiceFeeState>({
    serviceFeeActive: false,
  });

  // state to track the selected fee
  const [selectedFee, setSelectedFee] = useState<any>(null);

  // Function: Restarting the amount of service fee every time the user uses the cancel button
  function cancelClickServiceFee() {
    setServiceFeeState(prevState => ({
      ...prevState,
      serviceFeeActive: false,
    }));

    setSelectedFee(null);
  }

  // Function: Making the API call to put the Service Fee in the cart
  async function onAddServiceFee() {
    if (props.handleAddServiceFee) {
      void props.handleAddServiceFee?.(Number(selectedFee?.id));
      cancelClickServiceFee();
    } else {
      const params = {
        service_fee_id: selectedFee?.id,
        facility_id: facilityStore?.facility?.id,
        cart_id: cartStore?.cart?.id,
      };

      const cartRes = await PostServiceFeeLineItem(params, true);

      const cart = cartRes.data;

      dispatch({
        type: "cart.update",
        payload: {
          isLoaded: true,
          cart: cart,
        },
      });

      cancelClickServiceFee();
    }
  }

  async function onClearServiceFee() {
    const params = {
      // service_fee_id: selectedFee?.id,
      facility_id: facilityStore?.facility?.id,
      cart_id: cartStore?.cart?.id,
    };

    const cartRes = await DeleteServiceFeeLineItem(params, true);

    const cart = cartRes.data;

    dispatch({
      type: "cart.update",
      payload: {
        isLoaded: true,
        cart: cart,
      },
    });

    cancelClickServiceFee();
  }

  function onClickServiceFee(fee: any) {
    setSelectedFee(fee);
  }

  async function handleServiceFeeClick() {
    if (facilityStore?.serviceFees?.length > 1) {
      setServiceFeeState(prevState => ({ ...prevState, serviceFeeActive: true }));
    } else {
      if (props.handleAddServiceFee) {
        void props.handleAddServiceFee?.(Number(facilityStore?.serviceFees[0]?.id));
        cancelClickServiceFee();
      } else {
        const params = {
          service_fee_id: facilityStore?.serviceFees[0]?.id,
          facility_id: facilityStore?.facility?.id,
          cart_id: cartStore?.cart?.id,
        };

        const cartRes = await PostServiceFeeLineItem(params, true);

        const cart = cartRes.data;

        dispatch({
          type: "cart.update",
          payload: {
            isLoaded: true,
            cart: cart,
          },
        });

        cancelClickServiceFee();
      }
    }
  }

  return (
    <div>
      <div className="flex">
        {(props.totalServiceFeeAmount > 0 && props.totalServiceFeeAmount !== null) ||
        props?.order ||
        cartStore?.cart?.status === "void" ? (
          <div
            className={classNames("ml-auto", { "cursor-pointer": !props.order })}
            onClick={
              cartStore?.cart?.status !== "complete" && !props?.order && cartStore?.cart?.status !== "void"
                ? () => handleServiceFeeClick()
                : undefined
            }
          >
            <LocaleCurrency currency="cad" amount={props.totalServiceFeeAmount} />
          </div>
        ) : (
          cartStore?.cart?.status !== "complete" && (
            <div className="cursor-pointer" onClick={() => handleServiceFeeClick()}>
              <span style={{ fontWeight: "600", color: "#203254", fontSize: props.smallerText ? "14px" : null }}>
                {" "}
                <FontAwesomeIcon icon={faPlus} /> {t("Add Service Fee")}
              </span>
            </div>
          )
        )}
      </div>

      {serviceFeeState?.serviceFeeActive ? (
        <Sheet
          title={t("Add Service Fee")}
          open={serviceFeeState?.serviceFeeActive}
          size="small"
          closable
          onCancel={cancelClickServiceFee}
          onOk={onAddServiceFee}
          onSecondary={onClearServiceFee}
          secondaryText={"Clear"}
          okText={t("Add Service Fee")}
          darkStyle={props?.darkStyle ? true : false}
          darkerButton={props.darkerButton}
        >
          {facilityStore?.serviceFees?.map((fee: any, index: number) => {
            const isSelected = selectedFee?.id === fee?.id;
            const percentage = `${(fee?.percent * 100).toFixed(2)}%`;

            return (
              <div
                key={index}
                className={`service-fee-container ${isSelected ? "selected" : ""}`}
                onClick={() => onClickServiceFee(fee)}
              >
                <p>{fee?.title}</p>
                <p>{valueToString(String(fee?.type))}</p>
                <p>{fee?.type === "fixed_amount" ? "$" + fee?.fixed_amount : percentage}</p>
              </div>
            );
          })}
        </Sheet>
      ) : null}
    </div>
  );
}
